import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { fetchHappiness } from "../lib/api";
import { safeUrl } from "../lib/safeUrl";

export const HappinessComparison = () => {
  const [d, setD] = useState(null);
  useEffect(() => { fetchHappiness().then(setD).catch(() => {}); }, []);
  if (!d) return <div className="h-72 animate-pulse rounded-3xl bg-white" />;
  const w = d.world_happiness_report;
  const s = d.statcan;
  return (
    <section className="grid gap-6 lg:grid-cols-5" data-testid="happiness-comparison">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 lg:col-span-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-800">{w.edition}</p>
        <div className="mt-3 flex items-end gap-3">
          <span className="font-serif text-5xl text-slate-900" data-testid="whr-canada-rank">#{w.canada.rank}</span>
          <span className="mb-1.5 text-sm text-slate-500">of ~140 countries · score {w.canada.score.toFixed(2)} / 10</span>
        </div>
        <ul className="mt-6 space-y-2.5">
          {w.peers.map((p) => (
            <li key={p.country} className="grid grid-cols-[7.5rem_1fr_3rem] items-center gap-3 text-sm" data-testid={`whr-peer-${p.country.toLowerCase().replace(/\s/g, "-")}`}>
              <span className={p.country === "Canada" ? "font-semibold text-sky-900" : "text-slate-600"}>{p.country}</span>
              <div className="h-2 rounded-full bg-slate-100">
                <div className={`h-2 rounded-full ${p.country === "Canada" ? "bg-sky-700" : "bg-sky-200"}`} style={{ width: `${((p.score - 5) / 3) * 100}%` }} />
              </div>
              <span className="text-right font-mono text-xs text-slate-500">#{p.rank}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>Canada's rank over time:</span>
          {w.canada_history.map((h) => <span key={h.year} className="rounded-md bg-slate-100 px-2 py-0.5 font-mono">{h.year}: #{h.rank}</span>)}
        </div>
        <p className="mt-4 text-xs text-slate-500">{w.note} <a href={safeUrl(w.source_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-800 underline" data-testid="whr-source-link">Source <ExternalLink size={11} /></a></p>
      </div>
      <div className="flex flex-col rounded-3xl border border-emerald-100 bg-emerald-50/60 p-6 sm:p-8 lg:col-span-2">
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-800">{s.title}</p>
        <span className="mt-3 font-serif text-5xl text-slate-900" data-testid="statcan-average">{s.average_score}<span className="text-2xl text-slate-500"> / 10</span></span>
        <p className="mt-1 text-sm text-slate-600">average life satisfaction</p>
        <p className="mt-6 font-serif text-3xl text-emerald-900">~{s.high_satisfaction_share}%</p>
        <p className="text-sm text-slate-600">{s.high_satisfaction_label}</p>
        <p className="mt-auto pt-6 text-xs text-slate-500">{s.note} <a href={safeUrl(s.source_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-800 underline" data-testid="statcan-source-link">Source <ExternalLink size={11} /></a></p>
      </div>
    </section>
  );
};
